import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  TablePagination,
  Stack, // Import Stack for better layout control
} from "@mui/material";

import { _get } from "../../Config/axiosInstance";

function BookingListPage() {
  const [bookingData, setBookingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    variant: "filled",
    severity: "success",
  });

  const fetchBookingData = async () => {
    try {
      const res = await _get("/bookinglist");
      const data = res.data.data || [];

      // Assuming movieDate is already ordered by the backend or needs client-side sort
      // If backend handles sorting (as per previous UserModels.js changes), this client-side sort might be redundant
      const sortedData = data.sort(
        (a, b) => new Date(b.movieDate) - new Date(a.movieDate)
      );

      setBookingData(sortedData);
      setFilteredData(sortedData);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch booking data.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingData();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching

    const filtered = bookingData.filter(
      (row) =>
        row.movieName.toLowerCase().includes(value) ||
        row.custName.toLowerCase().includes(value) ||
        row.custMobile.toString().includes(value) ||
        (row.offerName && row.offerName.toLowerCase().includes(value)) // Check if offerName exists
    );

    setFilteredData(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}> {/* Max width and center */}
      <Stack direction="column" spacing={3}> {/* Use Stack for vertical spacing */}
        <Typography variant="h4" component="h1" gutterBottom align="left" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Booking List
        </Typography>

        <TextField
          label="Search bookings by movie, customer name, mobile, or offer..."
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearch}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} // Rounded corners
        />

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ overflowX: "auto", mt: 2, borderRadius: 2, elevation: 5 }}> {/* Elevated, rounded corners */}
            <Table stickyHeader aria-label="booking table"> {/* stickyHeader for persistent header */}
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: 'primary.light', color: 'primary.contrastText', fontWeight: 'bold' } }}> {/* Styled header row */}
                  <TableCell>#</TableCell>
                  <TableCell>Movie Name</TableCell>
                  <TableCell>Movie Date</TableCell>
                  <TableCell>Tickets</TableCell>
                  <TableCell>Screen</TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Customer Mobile</TableCell>
                  <TableCell>Offer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => (
                  <TableRow
                    key={row.mId}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9', // Light gray for odd rows
                      },
                      '&:hover': {
                        backgroundColor: '#f0f0f0', // Darker gray on hover
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{row.movieName}</TableCell>
                    <TableCell>{new Date(row.movieDate).toLocaleString()}</TableCell>
                    <TableCell>{row.movieTickets}</TableCell>
                    <TableCell>{row.movieScreen}</TableCell>
                    <TableCell>{row.custName}</TableCell>
                    <TableCell>{row.custMobile}</TableCell>
                    <TableCell>{row.offerName || 'N/A'}</TableCell> {/* Display N/A if offerName is null */}
                  </TableRow>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      <Typography variant="subtitle1">No booking results found matching your search.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BookingListPage;